import {
  Response,
  GeneralLedgerTransaction,
  apiAbortController,
  Document,
  DocumentStatus,
  DocumentType,
  BaseTransaction,
  ChangeLog,
  Signature,
  DocumentComment,
} from '@ui/src';
import {
  EditNotificationsValues,
  NotificationItem,
  NotificationItemComment,
} from '@notifications/hoc/center/types';
import { Balance } from '../../../types/account';
import api from '../../../api/api';
import {
  Channel,
  CofProps,
  ChatBody,
  Employee,
  UploadResponse,
  CASP,
} from '../type';
import { Order, PaymentOrder } from '../../cash-collection/types';

export const transactionApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionsEmployee: build.query<
      {
        data: { id: string; profile: { firstName: string; lastName: string } };
      },
      string
    >({
      query: (id) => ({
        url: `/v1/employees/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCurrentEmployee: build.query<{ data: Employee }, void>({
      query: () => ({
        url: '/v1/employees/whoami',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGeneralLedgerTransactionValues: build.query<
      Response<GeneralLedgerTransaction>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions/${id}/general-ledger`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGeneralLedgerExpandedTransaction: build.query<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionChats: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/channels?transactionId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientsOfficias: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTransactionChat: build.mutation<
      { data: Channel },
      { id: string; body: ChatBody }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/channels`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    transactionsUpload: build.mutation({
      async queryFn(file: File, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('files', file);

        const response = (await fetchWithBQ({
          url: '/v1/uploads',
          method: 'POST',
          body: formData,
        })) as { data: UploadResponse[] };

        return response;
      },
    }),
    getTransactionDocumentById: build.query<
      { data: Document },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionComments: build.query<
      Response<DocumentComment>,
      { id: string; url: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments?take=20${
          data?.cursor ? `&cursor=${data.cursor}` : ''
        }&sortDir=desc&sortProp=createdAt`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsDocuments: build.query<
      Response<Document>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents${
          data.cursor ? `?cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCommentToTransaction: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        url: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editCommentInTransaction: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        url: string;
        commentId: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCommentInTransaction: build.mutation<
      { data: DocumentComment },
      { id: string; commentId: string; url: string }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addDocumentToTransaction: build.mutation<
      { data: Document },
      {
        id: string;
        body: {
          name: string;
          uploadId: string;
          organizationId?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeDocumentStatus: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; status: DocumentStatus } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeDocumentCategory: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; category: DocumentType } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addComment: build.mutation<any, { id: string; body: { text: string } }>({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editComment: build.mutation<
      any,
      { id: string; commentId: string; body: { text: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteComment: build.mutation<any, { id: string; commentId: string }>({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteDocuments: build.mutation<
      {
        data: { count: number };
      },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/batch-delete`,
        method: 'DELETE',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteTransaction: build.mutation<{ data: BaseTransaction }, string>({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllAccountalances: build.query<Response<Balance>, { id: string }>({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: '/v1/casps?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsNotificationsList: build.query<
      Response<NotificationItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/notifications/transactions${query}${
          query ? '&' : '?'
        }take=20&sortProp=createdAt&sortDir=desc`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsNotificationItem: build.query<
      { data: NotificationItem },
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsNotificationItemComment: build.query<
      Response<NotificationItemComment>,
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/transactions/${id}/comments?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addTransactionsNotificationItemComment: build.mutation<
      { data: NotificationItemComment },
      { id: string; body: { content: string } }
    >({
      query: (data) => ({
        url: `/v1/notifications/transactions/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTransactionsNotificationIssue: build.mutation<
      { data: NotificationItem },
      {
        id: string;
        body: {
          responsibleId?: string;
          executorId?: string;
          status?: string;
          deadline?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/transactions/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsNotificationChangeLogs: build.query<
      Response<ChangeLog>,
      string
    >({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionNotificationTransactionEntity: build.query<
      { data: any },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionNotificationCashCollectionEntity: build.query<
      { data: any },
      string
    >({
      query: (id) => ({
        url: `/v1/payments-orders/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchUpdateTransactionsNotificationIssue: build.mutation<
      { data: number },
      EditNotificationsValues
    >({
      query: (data) => ({
        url: '/v1/notifications/transactions/batch-update',
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsNotificationEmployees: build.query<
      Response<{
        id: string;
        profile: {
          firstName: string;
          lastName: string;
        };
      }>,
      void
    >({
      query: () => ({
        url: `/v1/employees?take=9999`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchAddTransactionsNotificationItemComment: build.mutation<
      { data: number },
      { ids: string[]; content: string }
    >({
      query: (data) => ({
        url: '/v1/notifications/transactions/batch-comments',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTransactionsNotificationIssueComment: build.mutation<
      { data: NotificationItemComment },
      {
        id: string;
        commentId: string;
        body: {
          content: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/transactions/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsImportHistory: build.query<Response<PaymentOrder>, string>({
      query: (query) => ({
        url: `/v1/payments-orders${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsImportItem: build.query<{ data: PaymentOrder }, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsImportItemOrders: build.query<
      { data: { beneficiaries: Order[] } },
      string
    >({
      query: (id) => ({
        url: `/v1/payments-orders/${id}/extended`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    importIncomingTransactions: build.mutation<{ data: any }, any>({
      query: (data) => ({
        url: '/v1/payments-orders/import-incoming-transactions',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createImportSignature: build.mutation<
      { data: Signature },
      {
        id: string;
        body: {
          mfaToken: string;
          signedAt: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/sign`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getImportItemSignatures: build.query<Response<Signature>, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}/signatures`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    rejectImportItem: build.mutation<
      { data: PaymentOrder },
      {
        body: {
          status: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetTransactionsEmployeeQuery,
  useLazyGetGeneralLedgerTransactionValuesQuery,
  useLazyGetGeneralLedgerExpandedTransactionQuery,
  useLazyGetTransactionChatsQuery,
  useLazyGetClientsOfficiasQuery,
  useCreateTransactionChatMutation,
  useGetCurrentEmployeeQuery,
  useTransactionsUploadMutation,
  useLazyGetTransactionsDocumentsQuery,
  useAddDocumentToTransactionMutation,
  useLazyGetTransactionDocumentFileQuery,
  useLazyGetTransactionDocumentByIdQuery,
  useChangeDocumentStatusMutation,
  useChangeDocumentCategoryMutation,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useDeleteDocumentsMutation,
  useDeleteTransactionMutation,
  useLazyGetAllAccountalancesQuery,
  useLazyGetTransactionsCASPQuery,
  useLazyGetTransactionChangeLogsQuery,
  useLazyGetTransactionsNotificationsListQuery,
  useLazyGetTransactionsNotificationItemCommentQuery,
  useAddTransactionsNotificationItemCommentMutation,
  useUpdateTransactionsNotificationIssueMutation,
  useLazyGetTransactionsNotificationChangeLogsQuery,
  useLazyGetTransactionNotificationTransactionEntityQuery,
  useBatchAddTransactionsNotificationItemCommentMutation,
  useBatchUpdateTransactionsNotificationIssueMutation,
  useUpdateTransactionsNotificationIssueCommentMutation,
  useLazyGetTransactionsNotificationEmployeesQuery,
  useLazyGetTransactionsNotificationItemQuery,
  useLazyGetTransactionsImportHistoryQuery,
  useImportIncomingTransactionsMutation,
  useLazyGetTransactionsImportItemQuery,
  useLazyGetTransactionsImportItemOrdersQuery,
  useCreateImportSignatureMutation,
  useLazyGetImportItemSignaturesQuery,
  useRejectImportItemMutation,
  useLazyGetTransactionNotificationCashCollectionEntityQuery,
  useLazyGetTransactionCommentsQuery,
  useAddCommentToTransactionMutation,
  useEditCommentInTransactionMutation,
  useDeleteCommentInTransactionMutation,
} = transactionApi;

export const {
  endpoints: {
    getTransactionsEmployee,
    getGeneralLedgerTransactionValues,
    getGeneralLedgerExpandedTransaction,
    getTransactionChats,
    getClientsOfficias,
    createTransactionChat,
    getCurrentEmployee,
    transactionsUpload,
    getTransactionsDocuments,
    addDocumentToTransaction,
    getTransactionDocumentFile,
    getTransactionDocumentById,
    changeDocumentStatus,
    changeDocumentCategory,
    deleteDocuments,
    deleteTransaction,
    getAllAccountalances,
    getTransactionsCASP,
    getTransactionChangeLogs,
    getTransactionsNotificationsList,
    addTransactionsNotificationItemComment,
    getTransactionsNotificationItemComment,
    updateTransactionsNotificationIssue,
    getTransactionsNotificationChangeLogs,
    getTransactionNotificationTransactionEntity,
    batchAddTransactionsNotificationItemComment,
    batchUpdateTransactionsNotificationIssue,
    updateTransactionsNotificationIssueComment,
    getTransactionsNotificationItem,
    getTransactionsNotificationEmployees,
    getTransactionsImportHistory,
    importIncomingTransactions,
    getTransactionsImportItem,
    getTransactionsImportItemOrders,
    createImportSignature,
    getImportItemSignatures,
    rejectImportItem,
    getTransactionNotificationCashCollectionEntity,
    getTransactionComments,
    addCommentToTransaction,
    editCommentInTransaction,
    deleteCommentInTransaction,
  },
} = transactionApi;
